import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  IconButton,
  LinearProgress,
  Divider,
  Paper,
} from "@mui/material";
import MDTypography from "shared/components/MDTypography";
import MDButton from "shared/components/MDButton";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import ErrorIcon from "@mui/icons-material/Error";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningIcon from "@mui/icons-material/Warning";
import DescriptionIcon from "@mui/icons-material/Description";
import ScheduleIcon from "@mui/icons-material/Schedule";
import QrCodeIcon from "@mui/icons-material/QrCode";

const colors = {
  primary: "#1e293b",
  secondary: "#0b4f6c",
  light: "#1e7e6c",
  white: "#ffffff",
  gray: "#f8fafc",
  lightGray: "#e2e8f0",
  darkGray: "#1e293b",
  error: "#d32f2f",
  success: "#27ae60",
  info: "#2980b9",
  warning: "#ed6c02",
};

const ConfirmacionImportacionTags = ({
  open,
  onClose,
  importing,
  importResult,
  fileName,
  importProgress = 0,
}) => {
  // ==================== ESTADO: IMPORTANDO ====================
  if (importing) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
        disableEscapeKeyDown
        disableBackdropClick
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CircularProgress size={28} sx={{ color: colors.white }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Procesando Tags
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                Por favor espere mientras se procesa el archivo
              </MDTypography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 5, px: 4, textAlign: "center" }}>
          <Box position="relative" display="inline-block" mb={3}>
            <Box
              position="absolute"
              top="50%"
              left="50%"
              sx={{ transform: "translate(-50%, -50%)" }}
            >
              <CloudUploadIcon sx={{ fontSize: 38, color: colors.secondary, opacity: 0.8 }} />
            </Box>
          </Box>

          <MDTypography variant="h5" fontWeight="bold" color={colors.darkGray} gutterBottom>
            Procesando archivo...
          </MDTypography>

          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1.5,
              px: 3,
              py: 1,
              bgcolor: colors.gray,
              borderRadius: 3,
              mt: 2,
            }}
          >
            <DescriptionIcon sx={{ fontSize: 20, color: colors.secondary }} />
            <MDTypography variant="body1" color="text.secondary">
              {fileName || "Archivo Excel"}
            </MDTypography>
          </Box>

          <Box sx={{ width: "100%", mt: 4 }}>
            <Box display="flex" justifyContent="space-between" mb={1}>
              <MDTypography variant="caption" color="text.secondary">
                Envío del archivo
              </MDTypography>
              <MDTypography variant="caption" fontWeight="bold" color={colors.secondary}>
                {importProgress > 0 && importProgress < 100
                  ? `${Math.round(importProgress)}%`
                  : "En curso"}
              </MDTypography>
            </Box>
            <LinearProgress
              variant={importProgress > 0 && importProgress < 100 ? "determinate" : "indeterminate"}
              value={importProgress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: `${colors.light}30`,
                "& .MuiLinearProgress-bar": {
                  backgroundColor: colors.secondary,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          <MDTypography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            {importProgress > 0 && importProgress < 100
              ? `Enviando archivo… ${Math.round(importProgress)}%`
              : importProgress >= 100
              ? "Archivo enviado. Esperando confirmación del servidor…"
              : "Validando el archivo y comprobando duplicados…"}
          </MDTypography>
        </DialogContent>
      </Dialog>
    );
  }

  // ==================== ESTADO: ÉXITO ====================
  if (importResult?.success) {
    const hasValidCount = importResult.importedCount && importResult.importedCount > 0;
    const displayCount = hasValidCount ? importResult.importedCount : null;

    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.success} 0%, ${colors.light} 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <CheckCircleIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Importación Exitosa
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                Los tags se han importado correctamente
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.success}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <CheckCircleOutlineIcon sx={{ fontSize: 80, color: colors.success }} />
            </Box>
            <MDTypography variant="h4" fontWeight="bold" color={colors.success} gutterBottom>
              ¡Completado!
            </MDTypography>
          </Box>

          <Divider sx={{ my: 3 }} />

          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: colors.gray,
              borderRadius: 3,
            }}
          >
            <MDTypography variant="subtitle1" fontWeight="bold" color={colors.primary} mb={2}>
              Resumen de la importación
            </MDTypography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <DescriptionIcon sx={{ fontSize: 22, color: colors.secondary }} />
                <MDTypography variant="body1" color="text.secondary">
                  Archivo:
                </MDTypography>
              </Box>
              <MDTypography variant="body1" fontWeight="medium" color={colors.darkGray}>
                {fileName || "desconocido"}
              </MDTypography>
            </Box>

            <Divider sx={{ my: 1.5 }} />

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                py: 1.5,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <QrCodeIcon sx={{ fontSize: 22, color: colors.success }} />
                <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                  Estado:
                </MDTypography>
              </Box>
              <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                Importado correctamente
              </MDTypography>
            </Box>
          </Paper>

          {displayCount !== null && displayCount > 0 && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#e8f5e9",
                borderRadius: 3,
                textAlign: "center",
              }}
            >
              <MDTypography variant="body1" fontWeight="bold" color={colors.success}>
                Total de tags importados: {displayCount}
              </MDTypography>
            </Box>
          )}

          {importResult.validationSummary && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                bgcolor: "#e3f2fd",
                borderRadius: 3,
              }}
            >
              <MDTypography variant="subtitle2" fontWeight="bold" color={colors.info} mb={1.5}>
                Detalles de validación:
              </MDTypography>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <MDTypography variant="caption" color="text.secondary">
                  Total registros:
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold">
                  {importResult.validationSummary.totalRows || 0}
                </MDTypography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <MDTypography variant="caption" color="text.secondary">
                  Registros válidos:
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold" color={colors.success}>
                  {importResult.validationSummary.validRows || 0}
                </MDTypography>
              </Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <MDTypography variant="caption" color="text.secondary">
                  Registros inválidos:
                </MDTypography>
                <MDTypography variant="caption" fontWeight="bold" color={colors.error}>
                  {importResult.validationSummary.invalidRows || 0}
                </MDTypography>
              </Box>
              {importResult.validationSummary.duplicateInFile > 0 && (
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="caption" color="text.secondary">
                    Duplicados en archivo:
                  </MDTypography>
                  <MDTypography variant="caption" fontWeight="bold" color={colors.warning}>
                    {importResult.validationSummary.duplicateInFile}
                  </MDTypography>
                </Box>
              )}
              {importResult.validationSummary.duplicateInDatabase > 0 && (
                <Box display="flex" justifyContent="space-between">
                  <MDTypography variant="caption" color="text.secondary">
                    Duplicados en sistema:
                  </MDTypography>
                  <MDTypography variant="caption" fontWeight="bold" color={colors.error}>
                    {importResult.validationSummary.duplicateInDatabase}
                  </MDTypography>
                </Box>
              )}
            </Paper>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.success,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                bgcolor: colors.light,
              },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  // ==================== ESTADO: ERROR ====================
  if (importResult?.success === false && importResult) {
    return (
      <Dialog
        open={open}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0,0,0,0.25)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: `linear-gradient(135deg, ${colors.error} 0%, #c62828 100%)`,
            color: colors.white,
            py: 3,
            px: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <ErrorIcon sx={{ fontSize: 28 }} />
            <Box>
              <MDTypography variant="h5" fontWeight="bold" color={colors.white}>
                Error en la Importación
              </MDTypography>
              <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.5 }}>
                No se pudo importar el archivo
              </MDTypography>
            </Box>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: colors.white }}>
            <CloseIcon sx={{ fontSize: 24 }} />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 4 }}>
          <Box textAlign="center" mb={3}>
            <Box
              sx={{
                display: "inline-flex",
                p: 2.5,
                borderRadius: "50%",
                bgcolor: `${colors.error}10`,
                mt: 1,
                mb: 2,
              }}
            >
              <ErrorIcon sx={{ fontSize: 80, color: colors.error }} />
            </Box>

            <MDTypography variant="h4" fontWeight="bold" color={colors.error} gutterBottom>
              ¡Algo salió mal!
            </MDTypography>

            <Paper
              elevation={0}
              sx={{
                bgcolor: "#ffebee",
                borderRadius: 3,
                p: 2.5,
                mt: 2,
                border: `1px solid #ffcdd2`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <WarningIcon sx={{ fontSize: 22, color: colors.error }} />
                <MDTypography variant="body1" fontWeight="medium" color={colors.error}>
                  {importResult.error || "Error al importar el archivo"}
                </MDTypography>
              </Box>
            </Paper>
          </Box>

          {/* Mostrar errores de validación detallados */}
          {importResult.validationErrors && importResult.validationErrors.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: "#fff3e0",
                borderRadius: 3,
                mb: 2,
                border: `1px solid #ffe082`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <WarningIcon sx={{ fontSize: 22, color: colors.warning }} />
                <MDTypography variant="subtitle1" fontWeight="bold" color={colors.warning}>
                  Errores de validación:
                </MDTypography>
              </Box>
              <Box sx={{ maxHeight: 200, overflow: "auto" }}>
                {importResult.validationErrors.slice(0, 10).map((err, idx) => (
                  <MDTypography
                    key={idx}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ py: 0.5, fontFamily: "monospace" }}
                  >
                    • {err}
                  </MDTypography>
                ))}
                {importResult.validationErrors.length > 10 && (
                  <MDTypography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ mt: 1 }}
                  >
                    ... y {importResult.validationErrors.length - 10} errores más
                  </MDTypography>
                )}
              </Box>
            </Paper>
          )}

          {/* Mostrar duplicados en archivo */}
          {importResult.duplicateInFile && importResult.duplicateInFile.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: "#fff3e0",
                borderRadius: 3,
                mb: 2,
                border: `1px solid #ffe082`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <WarningIcon sx={{ fontSize: 22, color: colors.warning }} />
                <MDTypography variant="subtitle1" fontWeight="bold" color={colors.warning}>
                  Duplicados en el archivo ({importResult.duplicateInFile.length})
                </MDTypography>
              </Box>
              <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                {importResult.duplicateInFile.slice(0, 5).map((dup, idx) => (
                  <MDTypography
                    key={idx}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ py: 0.5, fontFamily: "monospace" }}
                  >
                    • Fila {dup.row}: {dup.type} &quot;{dup.value}&quot; duplicado
                  </MDTypography>
                ))}
              </Box>
            </Paper>
          )}

          {/* Mostrar duplicados en BD */}
          {importResult.duplicateInDatabase && importResult.duplicateInDatabase.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                p: 2.5,
                bgcolor: "#ffebee",
                borderRadius: 3,
                mb: 2,
                border: `1px solid #ffcdd2`,
              }}
            >
              <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
                <ErrorIcon sx={{ fontSize: 22, color: colors.error }} />
                <MDTypography variant="subtitle1" fontWeight="bold" color={colors.error}>
                  Duplicados en el sistema ({importResult.duplicateInDatabase.length})
                </MDTypography>
              </Box>
              <Box sx={{ maxHeight: 150, overflow: "auto" }}>
                {importResult.duplicateInDatabase.slice(0, 5).map((dup, idx) => (
                  <MDTypography
                    key={idx}
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ py: 0.5, fontFamily: "monospace" }}
                  >
                    • Fila {dup.row}: {dup.type} &quot;{dup.value}&quot; ya existe
                  </MDTypography>
                ))}
              </Box>
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: colors.gray,
              borderRadius: 3,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <ScheduleIcon sx={{ fontSize: 22, color: colors.info }} />
              <MDTypography variant="subtitle1" fontWeight="bold" color={colors.primary}>
                Posibles soluciones:
              </MDTypography>
            </Box>
            <Box component="ul" sx={{ margin: 0, paddingLeft: "1.75rem" }}>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Verifique que el archivo sea de tipo <strong>.xlsx</strong> o{" "}
                  <strong>.xls</strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Asegúrese que el archivo contenga las columnas:{" "}
                  <strong>
                    Reverse Number, Number Below the QR, Code QR, ESTADO, LOTE, ESTADO TAGS
                  </strong>
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Verifique que no haya valores duplicados en el archivo
                </MDTypography>
              </li>
              <li style={{ marginBottom: "10px" }}>
                <MDTypography variant="body2" color="text.secondary">
                  Asegúrese que los códigos QR no existan previamente en el sistema
                </MDTypography>
              </li>
              <li>
                <MDTypography variant="body2" color="text.secondary">
                  Intente nuevamente en unos momentos
                </MDTypography>
              </li>
            </Box>
          </Paper>
        </DialogContent>

        <DialogActions
          sx={{
            p: 3,
            justifyContent: "center",
            bgcolor: colors.gray,
            borderTop: `1px solid ${colors.lightGray}`,
          }}
        >
          <MDButton
            variant="contained"
            onClick={onClose}
            size="medium"
            sx={{
              bgcolor: colors.error,
              color: colors.white,
              px: 5,
              py: 1,
              borderRadius: 2,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              "&:hover": {
                bgcolor: "#c62828",
              },
            }}
          >
            Cerrar
          </MDButton>
        </DialogActions>
      </Dialog>
    );
  }

  return null;
};

ConfirmacionImportacionTags.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  importing: PropTypes.bool,
  importResult: PropTypes.shape({
    success: PropTypes.bool,
    message: PropTypes.string,
    error: PropTypes.string,
    importedCount: PropTypes.number,
    validationErrors: PropTypes.array,
    duplicateInFile: PropTypes.array,
    duplicateInDatabase: PropTypes.array,
    validationSummary: PropTypes.shape({
      totalRows: PropTypes.number,
      validRows: PropTypes.number,
      invalidRows: PropTypes.number,
      duplicateInFile: PropTypes.number,
      duplicateInDatabase: PropTypes.number,
    }),
  }),
  fileName: PropTypes.string,
  importProgress: PropTypes.number,
};

ConfirmacionImportacionTags.defaultProps = {
  importing: false,
  importResult: null,
  fileName: "",
  importProgress: 0,
};

export default ConfirmacionImportacionTags;
